import { useTranslation } from 'react-i18next';
import { formatMonthDay } from '../utils/formatters';
import { CalendarIcon } from './core';

export interface ResolvedCrop {
  /** "{cropTypeId}_{regionId}" — used as a unique key */
  id: string;
  cropTypeName: string;
  regionName: string;
  seasonStart?: { month: number; day: number };
  seasonEnd?: { month: number; day: number };
}

interface CropsMiniTableProps {
  crops: ResolvedCrop[];
  compact?: boolean;
  /** If provided, a 🔔 remind pill appears on each row. Called with the crop id. */
  onRemind?: (_cropId: string) => void;
}

/** Shared crops grid table used by CustomerCropsSection (desktop) and the mobile inline view. */
export function CropsMiniTable({ crops, compact = false, onRemind }: CropsMiniTableProps) {
  const { t } = useTranslation();

  const cols = onRemind ? 'grid-cols-4' : 'grid-cols-3';

  const headerClass = compact
    ? `grid ${cols} border-b-thick border-green-200 bg-green-50 px-2.5 py-[5px] font-body text-[0.68rem] font-bold uppercase tracking-[.06em] text-green-800`
    : `grid ${cols} border-b-thick border-green-200 bg-green-50 px-3.5 py-2 font-body text-xs font-bold uppercase tracking-[.06em] text-green-800`;

  const rowClass = compact
    ? `grid ${cols} border-b border-green-100 px-2.5 py-2 last:border-0`
    : `grid ${cols} border-b border-green-100 px-4.5 py-[13px] transition-agrobook duration-agrobook last:border-0 hover:bg-green-50`;

  return (
    <div className={`overflow-hidden border-thick border-green-200 bg-white ${compact ? 'rounded-sm' : 'rounded'}`}>
      <div className={headerClass}>
        <span>{t('customers.cropTypeCol')}</span>
        <span>{t('customers.regionCol')}</span>
        <span>{t('customers.seasonCol')}</span>
        {onRemind && <span>{t('reminder.setReminder')}</span>}
      </div>
      {crops.map((crop) => {
        const season =
          crop.seasonStart && crop.seasonEnd
            ? `${formatMonthDay(crop.seasonStart.month, crop.seasonStart.day, t)} – ${formatMonthDay(crop.seasonEnd.month, crop.seasonEnd.day, t)}`
            : '—';
        return (
          <div key={crop.id} className={rowClass}>
            <span className={`font-body font-medium text-gray-900 ${compact ? 'text-sm' : 'text-base'}`}>{crop.cropTypeName}</span>
            <span className={`font-body font-medium text-gray-900 ${compact ? 'text-sm' : 'text-base'}`}>{crop.regionName}</span>
            <span className={`font-body text-gray-500 ${compact ? 'text-xs' : 'text-sm'}`}>{season}</span>
            {onRemind && (
              <span className="flex justify-center">
                <button
                  type="button"
                  onClick={() => onRemind(crop.id)}
                  aria-label={t('reminder.remindForCrop', { crop: crop.cropTypeName, region: crop.regionName })}
                  className="flex items-center rounded px-2 py-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600"
                >
                  <span aria-hidden="true" className="inline-flex h-5 w-5 shrink-0"><CalendarIcon /></span>
                </button>
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface CustomerCropsSectionProps {
  crops: ResolvedCrop[];
  onRemind?: (_cropId: string) => void;
}

export function CustomerCropsSection({ crops, onRemind }: CustomerCropsSectionProps) {
  const { t } = useTranslation();

  return (
    <div className="mb-4 mt-5">
      <div className="mb-2.5">
        <h2 className="font-display text-lg font-semibold tracking-tight text-green-900">
          {t('customers.crops')}
        </h2>
      </div>
      {crops.length === 0 ? (
        <div className="overflow-hidden rounded border-thick border-green-200 bg-white">
          <p className="px-4.5 py-[13px] font-body text-sm text-gray-400">{t('customers.noCrops')}</p>
        </div>
      ) : (
        <CropsMiniTable crops={crops} onRemind={onRemind} />
      )}
    </div>
  );
}
